import React, { useState, useEffect } from "react";
import Modal from "../common/Modal";
import useGameBase from "../../hooks/useGameBase";
//import { ElevenLabsClient, play } from '@elevenlabs/elevenlabs-js';

const EMOTICONS = ["😀", "😂", "😍", "😮", "👍", "🔥", "👏", "🤔", "😎"];

// DecksOfDepravityGame - Cards Against Humanity style UI
const darkClass = "dark:bg-gray-900 dark:text-white";

function DecksOfDepravityGame({
  gameState,
  gamePlayers,
  playerId,
  roomConfig,
  user,
  socket,
  roomId,
}) {
  const { normalizedState, submitAction } = useGameBase({
    gameState,
    gamePlayers,
    user,
    socket,
    roomId,
  });
  //const elevenlabs = new ElevenLabsClient();
  // Get current player's socketId
  const socketId = user?.socketId || socket?.id || playerId;
  const players = gamePlayers || [];

  // Extract state values
  const hand = normalizedState.hands?.[socketId] || [];
  const playedCards = normalizedState.playedCards || {};
  const scores = normalizedState.scores || {};
  const winner = normalizedState.winner; // overall winner
  const roundWinner = normalizedState.roundWinner;
  const judgeIndex = normalizedState.judgeIndex;
  const round = normalizedState.round;
  const prompt = normalizedState.currentPrompt;
  const isJudge = players[judgeIndex]?.socketId === socketId;
  const judgeId = players[judgeIndex]?.socketId;
  const judgeName = players[judgeIndex]?.username;
  const [selectedCard, setSelectedCard] = useState(null);
  const [emoticonPopup, setEmoticonPopup] = useState({
    open: false,
    answerId: null,
    x: 0,
    y: 0,
  });

  // Use server revealStage state
  const revealStageActive = normalizedState.revealStage;
  // Reveal stage timer is now handled by the server. No client timer needed.
  // But show a client-side countdown until revealEndsAt (server sets this when judge picks)
  const [revealSecondsLeft, setRevealSecondsLeft] = useState(null);

  useEffect(() => {
    let timer = null;
    const update = () => {
      const endsAt = normalizedState.revealEndsAt;
      if (!revealStageActive || !endsAt) {
        setRevealSecondsLeft(null);
        return;
      }
      const msLeft = Math.max(0, endsAt - Date.now());
      setRevealSecondsLeft(Math.ceil(msLeft / 1000));
    };
    if (revealStageActive && normalizedState.revealEndsAt) {
      update();
      timer = setInterval(update, 250);
    }
    return () => {
      if (timer) clearInterval(timer);
      setRevealSecondsLeft(null);
    };
  }, [revealStageActive, normalizedState.revealEndsAt]);

  const handleSubmit = () => {
    if (selectedCard) {
      submitAction({ type: "submitResponse", card: selectedCard });
      setSelectedCard(null);
      try { window.scrollTo({ top: 0, behavior: "auto" }); } catch (e) {}
    }
  };

  const handleJudgePick = (pid) => {
    if (!allResponsesSubmitted) {
      return;
    }
    submitAction({ type: "judgePick", winnerId: pid });
    setSelectedCard(null); // Clear local selection so UI updates for reveal
  };

  // Handler for non-judge appreciation
  const handleAppreciate = (pid, event) => {
    // Use mouse position for popup placement
    const x = event.clientX + window.scrollX - 90; // Center popup horizontally (popup width/2)
    const y = event.clientY + window.scrollY + 12; // 12px below mouse
    setEmoticonPopup({ open: true, answerId: pid, x, y });
  };

  const handleEmoticonSelect = (emoticon) => {
    if (emoticonPopup.answerId) {
      submitAction({
        type: "appreciateAnswer",
        answerId: emoticonPopup.answerId,
        emoticon,
      });
    }
    setEmoticonPopup({ open: false, answerId: null });
  };

  // Responsive and UI improvements
  const pointsToWin = roomConfig?.pointsToWin || 5;
  // Find previous round winner and response
  const lastAction = normalizedState.lastAction;
  // Removed unused variables prevWinnerName and prevWinningCard
  if (lastAction?.type === "judgePick" && lastAction.winner) {
  // Removed unused variable prevWinner
  // prevWinnerName = prevWinner?.username; // removed unused variable
    // Find the card they played
  // prevWinningCard = normalizedState.playedCards?.[lastAction.winner]; // removed unused variable
  }

  // Waiting messages
  const allResponsesSubmitted = players
    .filter((p) => (p.id || p.socketId) !== judgeId)
    .every((p) => playedCards[p.id || p.socketId]);
  const waitingForPlayers = !isJudge && !playedCards[socketId];
  const waitingForJudge =
    !winner && allResponsesSubmitted && Object.keys(playedCards).length > 0;
  const judgeHasMadeDecision = !!roundWinner;

  return (
    <div className={`p-4 rounded ${darkClass} max-w-2xl mx-auto`}>
      <div className="flex flex-wrap justify-between items-center mb-2">
        <div className="mb-4">
          {players?.map((p) => (
            <React.Fragment key={p.id || p.socketId}>
              {(p.id || p.socketId) === judgeId && (
                <span className="font-bold">
                  Round <span className="text-blue-500">{round}</span> -{" "}
                  {p.username} judging
                </span>
              )}
            </React.Fragment>
          ))}
        </div>
        <div className="mb-4">
          <div className="font-semibold">
            First to <span className="text-green-500">{pointsToWin}</span>{" "}
            points wins
          </div>
        </div>
        {/* {prevWinnerName && prevWinningCard && (
            <div className="mb-2 p-2 bg-yellow-100 dark:bg-yellow-900 border border-yellow-400 dark:border-yellow-700 rounded text-sm">
              <span className="font-semibold">Previous round winner:</span>{" "}
              <span className="text-green-700 dark:text-green-300">
                {prevWinnerName}
              </span>{" "}
              <span className="font-semibold">with:</span>{" "}
              <span className="italic">{prevWinningCard}</span>
            </div>
          )} */}
        <div className="px-4 w-full py-2 rounded-lg border shadow-md transition-all duration-150 mb-4 bg-white dark:bg-gray-700 dark:text-white">
          <span
            className="text-lg"
            dangerouslySetInnerHTML={{
              __html: (prompt || "").replace("____", "______")
            }}
          />
        </div>

        {/* {winner && (
            <div className="text-green-500 font-bold mb-4 text-center text-lg">
              Winner:{" "}
              {players?.find((p) => (p.id || p.socketId) === winner)?.username}
            </div>
          )} */}

        {/* Hand and submit for non-judge before response submitted */}
        {!isJudge && !playedCards[socketId] && (
          <div className="mb-4">
            {waitingForPlayers && (
              <div className="mt-2 mb-2 text-red-500 dark:text-red-200  pulsate">
                Pick your best answer and then submit it...
              </div>
            )}
            <div className="font-semibold mb-2">Your Cards:</div>
            <div className="flex flex-wrap gap-3 justify-center">
              {hand?.map((card) => (
                <button
                  key={card}
                  className={`px-4 w-full py-2 rounded-lg border shadow-md transition-all duration-150 ${
                    selectedCard === card
                      ? "bg-blue-500 text-white scale-105 ring-2 ring-blue-400"
                      : "bg-white dark:bg-gray-700 dark:text-white hover:bg-blue-100 dark:hover:bg-blue-900"
                  } ${
                    !!playedCards[socketId]
                      ? "opacity-50 cursor-not-allowed"
                      : "hover:scale-105"
                  }`}
                  onClick={() => {
                    if (selectedCard === card) {
                      setSelectedCard(null);
                    } else {
                      setSelectedCard(card);
                    }
                  }}
                  disabled={!!playedCards[socketId]}
                  style={{
                    minWidth: "120px",
                    minHeight: "48px",
                    fontSize: "1rem",
                  }}
                >
                  <span
                    dangerouslySetInnerHTML={{ __html: card }}
                  />
                </button>
              ))}
            </div>
            <button
              className={`mt-4 px-4 py-2 rounded-lg w-full shadow transition-all ${
                selectedCard
                  ? "bg-green-600 text-white hover:bg-green-700"
                  : "bg-gray-400 text-gray-200 cursor-not-allowed"
              }`}
              onClick={handleSubmit}
              disabled={!selectedCard}
            >
              Submit Answer
            </button>
          </div>
        )}
        {/* Message after non-judge submits response */}
        {!isJudge && playedCards[socketId] && !judgeHasMadeDecision && (
          <div className="mt-2 text-red-500 dark:text-red-200  pulsate">
            Response submitted! Waiting for {judgeName} to judge...
          </div>
        )}
      </div>
      <div className="mb-4">
        {/* When all responses are in but no decision yet */}
        {(!allResponsesSubmitted || waitingForJudge) && !revealStageActive && (
          <div>
            {isJudge && allResponsesSubmitted && (
              <div className="mt-2 text-red-500 dark:text-red-200 pulsate">
                Pick your favourite!
              </div>
            )}
            {!allResponsesSubmitted && isJudge && (
              <div className="mt-2 mb-2 text-gray-500">
                Wait for all players to submit responses before you pick your
                favourite...
              </div>
            )}
            {allResponsesSubmitted &&
              waitingForJudge &&
              !judgeHasMadeDecision && (
                <div className="mt-2 mb-2 text-gray-500">
                  All responses are in! <b>{judgeName}</b> will now pick a
                  winner.
                </div>
              )}
            <div className="flex flex-col gap-3 items-center">
              <div className="font-bold">
                {allResponsesSubmitted
                  ? "All Submissions"
                  : "Submissions so far"}
              </div>
              {Object.entries(playedCards).length === 0 && (
                <div>Nothing yet...</div>
              )}
              {Object.entries(playedCards).map(([pid, card]) => {
                const playerName = players?.find(
                  (p) => (p.id || p.socketId) === pid
                )?.username;
                return (
                  <button
                    key={pid}
                    className="px-4 py-2 w-full rounded-lg border shadow-md bg-white dark:bg-gray-700 dark:text-white hover:bg-purple-100 dark:hover:bg-purple-900 transition-all duration-150 flex items-center justify-between"
                    onClick={(event) => {
                      if (isJudge) {
                        handleJudgePick(pid);
                      } else {
                        handleAppreciate(pid, event);
                      }
                    }}
                  >
                    
                      {allResponsesSubmitted
                        ? <span className="w-full" dangerouslySetInnerHTML={{ __html: card }} />
                        : "Answer hidden until all answers are in"}
                    
                    {judgeHasMadeDecision && (
                      <span className="font-bold ml-4 text-blue-700 dark:text-blue-300">
                        {playerName}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
      {/* Reveal stage after judge picks winner */}
      {revealStageActive && roundWinner && (
        <div className="mb-4">
          {revealSecondsLeft !== null && (
            <div className="text-center font-bold mb-2 text-xl text-red-600 dark:text-red-300">
              Next question in {revealSecondsLeft}s
            </div>
          )}
          <div className="font-semibold mb-2 text-center text-lg">{judgeName} has decided!</div>
          <div className="flex flex-col gap-3 items-center">
            {Object.entries(playedCards).map(([pid, card]) => {
              const playerObj = players?.find(
                (p) => (p.id || p.socketId) === pid
              );
              const isRoundWinner = roundWinner === pid;
              const emoticons = normalizedState.answerEmoticons?.[pid] || [];
              // Only allow appreciation if not judge
              const canAppreciate = !isJudge && socketId !== pid;
              return (
                <div
                  key={pid}
                  className={`w-full  items-center gap-2 ${
                    isRoundWinner
                      ? "bg-green-100 dark:bg-green-900 border-green-400 dark:border-green-700"
                      : "bg-white dark:bg-gray-700"
                  } border rounded-lg px-4 py-2 shadow-md`}
                  style={{ cursor: canAppreciate ? "pointer" : "default" }}
                  onClick={canAppreciate ? (event) => handleAppreciate(pid, event) : undefined}
                  title={canAppreciate ? "React with an emoticon" : undefined}
                >
                  
                  <div className="w-full">
                    <span dangerouslySetInnerHTML={{ __html: card }} />
                  </div>
                  {emoticons.length > 0 && (
                    <span className="ml-2">
                      {emoticons.map((e, i) => (
                        <span key={i}>{e}</span>
                      ))}
                    </span>
                  )}
                  <div className="w-full">
                  <span
                    className={`font-bold ${
                      isRoundWinner ? "text-green-700 dark:text-green-300" : ""
                    }`}
                  >
                    {playerObj?.username}
                  </span>
                  {isRoundWinner && (
                    <span className="ml-2 text-green-700 dark:text-green-300 font-bold">
                      - Round Winner!
                    </span>
                  )}
                  </div>
                </div>
              );
            })}
          </div>
          {winner && (
            <div className="mt-4 text-center text-green-700 dark:text-green-300 font-bold text-lg">
              {players?.find((p) => (p.id || p.socketId) === winner)?.username} wins the game!
            </div>
          )}
          {/* Next round message removed; server controls timing and round advancement */}
        </div>
      )}

      {/* Emoticon popup/modal */}
      {emoticonPopup.open && (
        <Modal
          show={true}
          title={null}
          onClose={() =>
            setEmoticonPopup({ open: false, answerId: null, x: 0, y: 0 })
          }
        >
          <div className="flex flex-col items-center p-2">
            <div className="mb-2 font-bold text-lg dark:text-gray-100 text-gray-900">
              Choose an emoticon:
            </div>
            <div className="flex gap-2 flex-wrap justify-center w-full mb-2">
              {EMOTICONS.map((em) => (
                <button
                  key={em}
                  className="text-2xl px-1 py-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700"
                  style={{
                    width: 32,
                    minWidth: 32,
                    maxWidth: 32,
                    overflow: "hidden",
                    background: "none",
                    border: "none",
                    color: "inherit",
                  }}
                  onClick={() => handleEmoticonSelect(em)}
                >
                  {em}
                </button>
              ))}
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

DecksOfDepravityGame.Guide = function DecksOfDepravityGameGuide() {
  return (
    <div>
      <h3 className="font-bold mb-2">How to play Decks of Depravity</h3>
      <ul className="list-disc ml-6">
        <li>Players take turns being the Judge.</li>
        <li>
          All other players submit a response card to fill in the blank on the
          prompt card.
        </li>
        <li>The Judge picks the funniest or best response.</li>
        <li>First player to X points wins.</li>
      </ul>
    </div>
  );
};

export default DecksOfDepravityGame;
